import { DatePipe } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { lastValueFrom } from 'rxjs';
import { Metadata } from '../../../../api/api-types/api-response.api';
import { DisabilityKind } from '../../../../api/app-types/disability-type.app';
import {
	DOCUMENT_CATEGORY,
	DocumentEntry,
	DocumentFile,
	DocumentNavItem,
} from '../../../../api/app-types/document.app';
import { CUSTOM_DATE_FORMATS } from '../../../../core/adapters/custom-date-format';
import { LvrDocumentItem } from '../../../../core/components/document-item/document-item';
import { LvrHeading } from '../../../../core/components/heading/heading';
import { CarouselElement, NavCarousel } from '../../../../core/components/nav-carousel/nav-carousel';
import { LvrPermissionDirective } from '../../../../shared/permissions/permission.directive';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { DisabilityTypeDeleteDialog } from '../disability-type-delete-dialog/disability-type-delete-dialog';
import { PERMISSION_RESOURCE } from '../../../../shared/permissions/permissions.types';

@Component({
	selector: 'lvr-disability-type-item-details',
	imports: [TranslocoPipe, NavCarousel, LvrHeading, DatePipe, LvrDocumentItem, LvrPermissionDirective],
	templateUrl: './disability-type-item-details.html',
	styleUrl: './disability-type-item-details.scss',
})
export class DisabilityTypeItemDetails {
	public readonly router = inject(Router);
	public readonly route = inject(ActivatedRoute);
	private readonly data = toSignal(this.route.data);
	private readonly dialog = inject(MatDialog);
	private readonly session = inject(SessionManagerService);

	private readonly clientId = computed(() => this.data()!['clientId'] as number);
	public readonly kindNeighbors = computed(() => this.data()!['kindNeighbors']);
	public readonly kind = computed(() => this.data()!['disabilityType'].data as DisabilityKind);
	public readonly metadata = computed(() => this.data()!['disabilityType'].metadata as Metadata);
	public readonly shouldNotBeDeleted = computed(() => this.metadata().editOnlyFields!.length > 0);
	public readonly backLink = computed(() => `/desk/client/${this.clientId()}/disabilities`);
	public readonly dateDisplayFormat = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly canEdit = hasPermission(this.session.permissions, ['Clients.Edit', 'Clients.Edit.PhaseOneOnly']);
	public readonly readPermissions: PERMISSION_RESOURCE[] = ['Clients.Read', 'Clients.Read.PhaseOneOnly'];

	// Map disability Type to CarouselElement for the Nav-Carousel component
	public readonly carouselElements = computed(() => {
		const neighbors = this.kindNeighbors();

		if (!neighbors) return [];

		return [neighbors.previous, neighbors.current, neighbors.next]
			.filter((dk): dk is DisabilityKind => !!dk)
			.map((dk) => ({
				id: dk.id!.toString(),
				label: dk.disabilityType?.remark ?? '-',
			}));
	});

	public readonly currentIndex = computed(() => {
		const kind = this.kind();
		const elements = this.carouselElements();

		if (!kind || elements.length === 0) return 0;

		const index = elements.findIndex((e) => e.id === kind.id!.toString());

		return index >= 0 ? index : 0;
	});

	/**
	 * Handles the navigation from the nav carousel.
	 * Updates the route based on the selected element.
	 */
	public onNavigate(event: { index: number; element: CarouselElement }) {
		this.router.navigate(['/desk/client', this.clientId(), 'disabilities', 'type', event.element.id]);
	}

	/**
	 * Open delete dialog, reroutes after deletion.
	 */
	public async openDialog() {
		await lastValueFrom(
			this.dialog
				.open(DisabilityTypeDeleteDialog, {
					width: '400px',
					data: {
						kind: this.kind(),
						clientId: this.clientId(),
						onDeleted: async () => {
							await this.router.navigate(['../../'], { relativeTo: this.route });
						},
					},
				})
				.afterClosed(),
		);
	}

	/**
	 * Navigates to 'edit' view.
	 */
	public openEdit() {
		this.router.navigate(['edit'], { relativeTo: this.route });
	}

	/**
	 * Checks if the given date string is valid and different from the default DateOnly.MinValue ('0001-01-01'),
	 * which is returned by the backend when no date is provided.
	 * @param input - Date string to validate.
	 * @returns true if the date is valid and not the default min/max value.
	 */
	public isValidDate(input?: Date | string): boolean {
		if (!input) return false;
		const date = new Date(input);
		if (Number.isNaN(date.getTime())) return false;

		const y = date.getUTCFullYear();
		const m = date.getUTCMonth();
		const d = date.getUTCDate();

		// Min (0001-01-01), Max (9999-12-31) y overflow (10000-01-01 or higher)
		const isMin = y === 1 && m === 0 && d === 1;
		const isMax = y === 9999 && m === 11 && d === 31;
		const isOverflow = y >= 10000; // catches 10000-01-01 shown by the date pipe

		return !(isMin || isMax || isOverflow);
	}

	public onFileClick(file: DocumentFile) {
		const navItems = this.buildDisabilityKindNavItems(this.kind());

		this.router.navigate(['/desk/client', this.clientId(), 'documents', file.id], { state: { navItems } });
	}

	public buildDocumentEntryFromDisabilityKind(doc: DocumentFile): DocumentEntry {
		const createdAt = this.kind().statusAsOf ?? new Date();

		return {
			id: this.kind().id,
			name: doc.name,
			category: DOCUMENT_CATEGORY.DISABILITY_KIND,
			createdAt: createdAt,
			externalFileReference: this.kind().documents ?? [],
			isReport: false,
			isFrozen: this.shouldNotBeDeleted(),
		};
	}

	private buildDisabilityKindNavItems(kind: DisabilityKind): DocumentNavItem[] {
		const files = kind.documents ?? [];
		const entryName = kind.disabilityType?.remark?.trim() || '-';
		const createdAt = kind.statusAsOf ?? new Date();

		return files.map((file) => ({
			file,
			entryId: kind.id,
			entryName,
			category: DOCUMENT_CATEGORY.DISABILITY_KIND,
			createdAt,
			isReport: false,
			isFrozen: this.shouldNotBeDeleted(),
		}));
	}
}
